import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApplicationApiService, NewRoadmapRequestDto, ProfitCenterDto, ProjectDto, RegionDashboardDto } from '../../../services/application-api.service';
import { forkJoin } from 'rxjs';
import { LoadingComponent } from '../../../components/loading/loading.component';
import {MatRadioModule} from '@angular/material/radio';
import { SearchProjectComponent } from '../../roadmap/search-project/search-project.component';

@Component({
  selector: 'app-new-roadmap',
  imports: [MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatCheckboxModule, FontAwesomeModule, ReactiveFormsModule, MatTooltipModule,
    MatRadioModule, LoadingComponent],
  templateUrl: './new-roadmap.component.html',
  styleUrl: './new-roadmap.component.scss'
})
export class NewRoadmapComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<NewRoadmapComponent>);
  private readonly data = inject<{ region: RegionDashboardDto }>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly apiService = inject(ApplicationApiService);
  private readonly dialog = inject(MatDialog);

  readonly region = this.data.region;
  readonly faSearch = faSearch;

  roadmapForm!: FormGroup;
  profitCenters: ProfitCenterDto[] = [];
  marketSegments: string[] = [];
  isLoading = signal(true);

  ngOnInit(): void {
    this.roadmapForm = this.fb.group({
      projectName: ['', Validators.required],
      projectNumber: ['', Validators.required],
      isProjectNumberTbd: [false],
      marketSegment: [null],
      profitCenter: [null],
      isVolumeOver10M: [null, Validators.required],
      leanChampionName: [''],
      selectedImApproverAppUserId: [''],
      opportunityId: ['']
    });

    forkJoin({
      profitCenters: this.apiService.apiProfitCenterGet(this.region.id!),
      marketSegments: this.apiService.apiProjectsMarketSegments()
    }).subscribe(({ profitCenters, marketSegments }) => {
      this.profitCenters = profitCenters;
      this.marketSegments = marketSegments;
      this.isLoading.set(false);
    });
  }

  onLinkClick(): void {
    const dialogRef = this.dialog.open(SearchProjectComponent, {
      height: '600px',
      width: '1000px'
    });

    dialogRef.afterClosed().subscribe((result: ProjectDto | null) => {
      if (!result) {
        return;
      }

      this.roadmapForm.patchValue({
        projectName: result.projectName,
        projectNumber: result.projectNumber,
        isProjectNumberTbd: false,
        marketSegment: result.marketSegment,
        profitCenter: result.profitCenterCode,
        isVolumeOver10M: result.gsrRevenue! >= 10000000 ? '1' : '0',
        opportunityId: result.opportunityId
      });
      this.roadmapForm.get('isProjectNumberTbd')?.disable();
      this.roadmapForm.get('projectNumber')?.disable();
      this.roadmapForm.get('marketSegment')?.disable();
      this.roadmapForm.get('profitCenter')?.disable();
      this.roadmapForm.get('isVolumeOver10M')?.disable();
    });
  }

  onProjectTbdChange(isTbd: boolean): void {
    if (isTbd) {
      this.roadmapForm.get('projectNumber')?.disable();
      this.roadmapForm.patchValue({projectNumber: 'TBD'});
    } else {
      this.roadmapForm.get('projectNumber')?.enable();
      this.roadmapForm.patchValue({projectNumber: ''});
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onContinueClick(): void {
    const formValue = this.roadmapForm.getRawValue();

    const newRoadmapRequest: NewRoadmapRequestDto = {
      projectName: formValue.projectName,
      projectNumber: formValue.projectNumber,
      marketSegment: formValue.marketSegment,
      profitCenterCode: formValue.profitCenter,
      isVolumeOver10M: formValue.isVolumeOver10M === '1',
      leanChampionName: formValue.leanChampionName,
      imApproverAppUserId: 0,
      opportunityId: formValue.opportunityId
    };

    this.dialogRef.close(newRoadmapRequest);
  }
}
